import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Bell, User, Shield, CreditCard, Check, Loader, Crown, X, AlertCircle, Sparkles, ChevronDown, ChevronUp, Headphones } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import SMSPreferences from '../SMSPreferences';
import DigestPreferences from '../DigestPreferences';
import PrioritySupport from '../PrioritySupport';
import { trackUpgradeStart, trackUpgradeComplete, trackCancelSubscription, trackViewPlans } from '../../utils/analytics';

const SettingsTab = ({ user, setUser }) => {
  const [processing, setProcessing] = useState(false);
  const [pollingSessionId, setPollingSessionId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [isSubscriptionExpanded, setIsSubscriptionExpanded] = useState(false);
  
  const annualDiscount = 20;
  
  const tiers = [
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      annualPrice: 0,
      period: null,
      highlight: false,
      features: [
        { text: 'Basic mortgage tracking', included: true },
        { text: 'Manual rate comparisons', included: true },
        { text: 'Up to 3 offers', included: true },
        { text: 'Basic calculators', included: true },
        { text: 'Renewal timeline', included: true },
        { text: 'Live rate updates', included: false },
        { text: 'Rate history & forecasts', included: false },
        { text: 'Weekly digest emails', included: false }
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      monthlyPrice: 9.99,
      annualPrice: 95.90,
      period: 'month',
      highlight: true,
      badge: 'Most Popular',
      features: [
        { text: 'Everything in Free', included: true },
        { text: 'Live rate updates', included: true },
        { text: 'Rate history & trends', included: true },
        { text: 'Rate forecasts', included: true },
        { text: 'Unlimited offers', included: true },
        { text: 'Weekly digest emails', included: true },
        { text: 'SMS notifications', included: true },
        { text: 'HELOC calculator', included: false }
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 19.99,
      annualPrice: 191.90,
      period: 'month',
      highlight: false,
      badge: 'Best Value',
      features: [
        { text: 'Everything in Premium', included: true },
        { text: 'Advanced HELOC calculator', included: true },
        { text: 'Unlimited scenarios', included: true },
        { text: 'Export PDF reports', included: true },
        { text: 'Priority support', included: true },
        { text: 'Advanced analytics', included: true },
        { text: 'Goal tracking', included: true },
        { text: 'Early access features', included: true }
      ]
    }
  ];
  
  const getPrice = (tier) => {
    if (billingPeriod === 'annual') {
      return tier.annualPrice;
    }
    return tier.monthlyPrice;
  };
  
  const getMonthlyEquivalent = (tier) => {
    if (billingPeriod === 'annual' && tier.annualPrice > 0) {
      return (tier.annualPrice / 12).toFixed(2);
    }
    return tier.monthlyPrice.toFixed(2);
  };
  
  const getAnnualSavings = (tier) => {
    if (tier.monthlyPrice === 0) return 0;
    return (tier.monthlyPrice * 12 - tier.annualPrice).toFixed(0);
  };
  
  const handleUpgrade = async (tier) => {
    if (tier === 'free') {
      setShowCancelConfirm(true);
      return;
    }
    
    if (user.subscription_tier === tier) {
      toast.info(`You are already subscribed to ${tier}`);
      return;
    }
    
    setProcessing(true);
    
    // Track upgrade start
    trackUpgradeStart(tier, billingPeriod);
    
    try {
      const originUrl = encodeURIComponent(window.location.origin);
      const response = await api.post(
        `/api/subscription/checkout?tier=${tier}&billing_period=${billingPeriod}&origin_url=${originUrl}`,
        {}
      );
      
      const { url, session_id } = response.data;
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout process');
      setProcessing(false);
    }
  };
  
  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await api.post('/api/subscription/cancel', {});
      
      if (setUser) {
        setUser({ ...user, subscription_tier: 'free' });
      }
      toast.success('Subscription cancelled. You now have a free account.');
      setShowCancelConfirm(false);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };
  
  const pollPaymentStatus = useCallback(async (sessionId, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setPollingSessionId(null);
      return;
    }
    
    try {
      const response = await api.get(`/api/subscription/status/${sessionId}`);
      
      if (response.data.status === 'completed') {
        toast.success('Payment successful! Your subscription has been upgraded.');
        if (setUser) {
          setUser(prev => ({ ...prev, subscription_tier: response.data.tier }));
        }
        setPollingSessionId(null);
        window.history.replaceState({}, '', '/dashboard?tab=settings');
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPollingSessionId(null);
      } else if (response.data.status === 'pending') {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error('Error verifying payment');
        setPollingSessionId(null);
      }
    }
  }, [setUser]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      setPollingSessionId(sessionId);
      setIsSubscriptionExpanded(true);
      pollPaymentStatus(sessionId, 0);
    }
  }, [pollPaymentStatus]);

  return (
    <div data-testid="settings-tab" className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-heading font-semibold">Settings</h2>
          <p className="text-muted-foreground text-sm">Manage your account and notification preferences</p>
        </div>
      </div>
      
      {/* Subscription & Billing - Collapsible */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <button
          onClick={() => setIsSubscriptionExpanded(!isSubscriptionExpanded)}
          data-testid="toggle-subscription-btn"
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-heading font-semibold">Subscription & Billing</h3>
              <p className="text-sm text-muted-foreground">
                Current plan: <span className="text-foreground font-medium capitalize">{user?.subscription_tier || 'Free'}</span>
              </p>
            </div>
          </div>
          {isSubscriptionExpanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </button>
        
        {/* Subscription Content */}
        <div className={`transition-all duration-300 ease-in-out ${isSubscriptionExpanded ? 'max-h-[2000px]' : 'max-h-0 overflow-hidden'}`}>
          <div className="p-6 border-t border-border space-y-6">
            {pollingSessionId && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-3">
                <Loader className="h-5 w-5 text-primary animate-spin" />
                <span className="text-sm">Verifying your payment...</span>
              </div>
            )}
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 p-4 bg-muted/30 rounded-xl">
              <span className={`text-sm font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Monthly
              </span>
              
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                className="relative w-14 h-7 bg-muted rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="billing-toggle"
              >
                <span 
                  className={`absolute top-1 w-5 h-5 bg-primary rounded-full transition-transform ${
                    billingPeriod === 'annual' ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
              
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium transition-colors ${billingPeriod === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Annual
                </span>
                <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                  <Sparkles className="h-3 w-3" />
                  Save {annualDiscount}%
                </span>
              </div>
            </div>
            
            {/* Plan Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tiers.map(tier => (
                <div
                  key={tier.id}
                  data-testid={`tier-${tier.id}`}
                  className={`relative bg-background border rounded-xl p-5 flex flex-col transition-all ${
                    user?.subscription_tier === tier.id
                      ? 'border-primary ring-2 ring-primary/20'
                      : tier.highlight
                      ? 'border-primary/50'
                      : 'border-border'
                  }`}
                >
                  {tier.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        tier.id === 'premium' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-purple-500 text-white'
                      }`}>
                        {tier.badge}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {tier.id !== 'free' && <Crown className={`h-4 w-4 ${tier.id === 'pro' ? 'text-purple-400' : 'text-primary'}`} />}
                      <h4 className="text-lg font-heading font-semibold">{tier.name}</h4>
                    </div>
                    
                    <div className="mb-3">
                      {tier.monthlyPrice === 0 ? (
                        <span className="text-2xl font-bold">Free</span>
                      ) : (
                        <div>
                          {billingPeriod === 'annual' ? (
                            <>
                              <div className="flex items-baseline gap-1">
                                <span className="text-2xl font-bold font-mono">${getMonthlyEquivalent(tier)}</span>
                                <span className="text-muted-foreground text-sm">/mo</span>
                              </div>
                              <p className="text-xs text-green-400 font-medium">Save ${getAnnualSavings(tier)}/yr</p>
                            </>
                          ) : (
                            <div>
                              <span className="text-2xl font-bold font-mono">${tier.monthlyPrice}</span>
                              <span className="text-muted-foreground text-sm">/mo</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <ul className="space-y-1.5 mb-4">
                      {tier.features.slice(0, 5).map((feature, idx) => (
                        <li key={idx} className={`flex items-start gap-2 text-xs ${!feature.included ? 'text-muted-foreground/60' : ''}`}>
                          {feature.included ? (
                            <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                          ) : (
                            <X className="h-3 w-3 text-muted-foreground/40 mt-0.5 flex-shrink-0" />
                          )}
                          <span>{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {user?.subscription_tier === tier.id ? (
                    <div className="space-y-1">
                      <button
                        disabled
                        className="w-full py-2 bg-muted text-muted-foreground rounded-lg text-sm font-medium cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                      {tier.id !== 'free' && (
                        <button
                          onClick={() => setShowCancelConfirm(true)}
                          className="w-full py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      data-testid={`upgrade-to-${tier.id}-btn`}
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={processing || pollingSessionId}
                      className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                        tier.id === 'free'
                          ? 'bg-muted hover:bg-muted/80'
                          : tier.highlight
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'bg-purple-500 text-white hover:bg-purple-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {processing ? (
                        <Loader className="h-4 w-4 animate-spin mx-auto" />
                      ) : tier.id === 'free' ? (
                        'Downgrade'
                      ) : user?.subscription_tier === 'free' ? (
                        'Upgrade'
                      ) : (
                        tier.monthlyPrice > tiers.find(t => t.id === user?.subscription_tier)?.monthlyPrice ? 'Upgrade' : 'Switch'
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Billing Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="p-3 bg-muted/30 rounded-lg">
                <span className="text-muted-foreground text-xs">Plan</span>
                <p className="font-medium capitalize">{user?.subscription_tier || 'Free'}</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <span className="text-muted-foreground text-xs">Currency</span>
                <p className="font-medium">CAD</p>
              </div>
              {user?.subscription_tier !== 'free' && (
                <>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <span className="text-muted-foreground text-xs">Cycle</span>
                    <p className="font-medium">Monthly</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <span className="text-muted-foreground text-xs">Status</span>
                    <p className="font-medium text-green-400">Active</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Account Info */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Account</h3>
            <p className="text-sm text-muted-foreground">Your account information</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">Email</p>
            <p className="font-medium">{user?.email || 'Not set'}</p>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">Name</p>
            <p className="font-medium">{user?.name || 'Not set'}</p>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">Subscription</p>
            <p className="font-medium capitalize">{user?.subscription_tier || 'Free'}</p>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">Member Since</p>
            <p className="font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-CA') : 'N/A'}</p>
          </div>
        </div>
      </div>
      
      {/* Weekly Digest Preferences */}
      <DigestPreferences user={user} />
      
      {/* SMS Preferences */}
      <SMSPreferences user={user} />
      
      {/* Email Preferences */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
            <Bell className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Email Notifications</h3>
            <p className="text-sm text-muted-foreground">Email alerts are always enabled for rate alerts you set up</p>
          </div>
        </div>
        
        <div className="p-4 bg-muted/30 rounded-lg">
          <p className="text-sm text-muted-foreground">
            Email notifications are sent to <span className="text-foreground font-medium">{user?.email}</span> when:
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            <li className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-primary"></div>
              Your rate alerts are triggered
            </li>
            <li className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-primary"></div>
              Important account updates
            </li>
            <li className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-primary"></div>
              Subscription changes
            </li>
          </ul>
        </div>
      </div>
      
      {/* Privacy */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 bg-green-500/10 rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Privacy & Security</h3>
            <p className="text-sm text-muted-foreground">Your data is secure</p>
          </div>
        </div>
        
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>• Your mortgage details are encrypted and stored securely</p>
          <p>• We never share your personal information with third parties</p>
          <p>• You can request data deletion at any time by contacting support</p>
        </div>
      </div>
      
      {/* Priority Support */}
      <PrioritySupport user={user} />
      
      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold">Cancel Subscription?</h3>
            </div>
            
            <p className="text-muted-foreground text-sm mb-6">
              Are you sure you want to cancel your subscription? You'll lose access to premium features including:
            </p>
            
            <ul className="space-y-2 mb-6 text-sm">
              <li className="flex items-center gap-2">
                <X className="h-4 w-4 text-red-400" />
                Live rate updates
              </li>
              <li className="flex items-center gap-2">
                <X className="h-4 w-4 text-red-400" />
                Rate history & forecasts
              </li>
              <li className="flex items-center gap-2">
                <X className="h-4 w-4 text-red-400" />
                Weekly digest emails
              </li>
              <li className="flex items-center gap-2">
                <X className="h-4 w-4 text-red-400" />
                SMS notifications
              </li>
            </ul>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 py-2.5 bg-muted hover:bg-muted/80 rounded-lg font-medium transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 py-2.5 bg-red-500 text-white hover:bg-red-600 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {cancelling ? (
                  <Loader className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
